import { Modal } from '@mantine/core';
import { type FC, type ReactNode } from 'react';

import classes from './DocumentAuthoringShell.module.css';

export type DocumentAuthoringShellProps = {
    opened: boolean;
    onClose: () => void;
    /** Names the document being written — "Create Order", "Edit Order PO-2026-0042". */
    title: ReactNode;
    /** The live document. Rendered on the sunk left pane. */
    preview: ReactNode;
    /** The fields that change it. Rendered on the right, scrolling independently. */
    form: ReactNode;
    /**
     * The footer buttons, supplied whole rather than as a `onSubmit` + label pair.
     *
     * Every document this shell will carry ends up wanting a different set — save and send,
     * save as draft, issue — and a shell that owned them would grow a flag per document. It
     * owns the bar; the caller owns what sits in it.
     */
    actions: ReactNode;
};

/**
 * Authoring a business document, with the document itself on screen while it is written.
 *
 * TWO PANES, SCROLLING INDEPENDENTLY. The form is much longer than the page it produces, and
 * tying the scrolls together would pull the document out of view exactly while somebody fills
 * in the fields that change it.
 *
 * NOT A GENERIC MODAL. It is deliberately full-screen and unclosable by backdrop click: half a
 * purchase order is worth nothing, and a stray click that discarded one would be the worst
 * thing this screen could do. Leaving is an explicit act — the ✕ or Cancel.
 *
 * Extracted from the Create Order flow, which was the only thing that had it, so the
 * production order could have the same treatment without a second copy drifting away from the
 * first. The layout is unchanged from that original.
 */
const DocumentAuthoringShell: FC<DocumentAuthoringShellProps> = ({
    opened,
    onClose,
    title,
    preview,
    form,
    actions,
}) => (
    <Modal opened={opened} onClose={onClose} fullScreen padding={0} withCloseButton={false}>
        <div className={classes.root}>
            <div className={classes.header}>
                <span className={classes.title}>{title}</span>
                <button type="button" onClick={onClose} className={classes.close} aria-label="Close">
                    ✕
                </button>
            </div>

            <div className={classes.body}>
                <div className={classes.previewPane}>{preview}</div>
                <div className={classes.formPane}>{form}</div>
            </div>

            <div className={classes.footer}>{actions}</div>
        </div>
    </Modal>
);

export default DocumentAuthoringShell;
